import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import API from "../../../services/api";
import {
  createBiometricMapping,
  deleteBiometricMapping,
  fetchBiometricDevices,
  fetchBiometricMappings,
  updateBiometricMapping,
} from "../../../Moduels/attendance/wfApi";

const empty = {
  device_id: "",
  employee_id: "",
  device_employee_code: "",
  verification_method: "fingerprint",
  enrollment_status: "active",
  rfid_card: "",
  face_registered: false,
  fingerprint_registered: false,
  pin_set: false,
};

export default function WfEmployeeDeviceMapping() {
  const [rows, setRows] = useState([]);
  const [devices, setDevices] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [form, setForm] = useState(empty);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setErr("");
    try {
      const [maps, devs, empRes] = await Promise.all([
        fetchBiometricMappings({ active_only: false }),
        fetchBiometricDevices(),
        API.get("/employees/", { params: { page_size: 500 } }),
      ]);
      setRows(maps);
      setDevices(devs);
      const empData = empRes.data;
      setEmployees(Array.isArray(empData) ? empData : empData?.items ?? empData?.data ?? []);
    } catch (e) {
      setErr(typeof e?.response?.data?.detail === "string" ? e.response.data.detail : e?.response?.data?.detail?.message || e.message);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const onCreate = async (e) => {
    e.preventDefault();
    if (!form.device_id || !form.employee_id || !form.device_employee_code.trim()) return;
    setBusy(true);
    setMsg("");
    setErr("");
    try {
      await createBiometricMapping({
        ...form,
        rfid_card: form.rfid_card || null,
      });
      setMsg("Mapping created.");
      setForm(empty);
      await load();
    } catch (ex) {
      setErr(typeof ex?.response?.data?.detail === "string" ? ex.response.data.detail : ex?.response?.data?.detail?.message || ex.message);
    } finally {
      setBusy(false);
    }
  };

  const toggleMethod = async (row, method) => {
    const patch = { verification_method: method };
    if (method === "face") patch.face_registered = true;
    if (method === "fingerprint") patch.fingerprint_registered = true;
    if (method === "pin") patch.pin_set = true;
    try {
      await updateBiometricMapping(row.map_id, patch);
      await load();
    } catch (ex) {
      setErr(typeof ex?.response?.data?.detail === "string" ? ex.response.data.detail : ex?.response?.data?.detail?.message || ex.message);
    }
  };

  const onRevoke = async (mapId) => {
    if (!window.confirm("Revoke this enrollment mapping?")) return;
    try {
      await deleteBiometricMapping(mapId, false);
      setMsg("Mapping revoked.");
      await load();
    } catch (ex) {
      setErr(typeof ex?.response?.data?.detail === "string" ? ex.response.data.detail : ex?.response?.data?.detail?.message || ex.message);
    }
  };

  const empLabel = (id) => {
    const e = employees.find((x) => String(x.employee_id) === String(id));
    if (!e) return id;
    return `${e.employee_code || ""} — ${e.display_name || e.first_name || ""}`.trim();
  };

  const deviceLabel = (id) => {
    const d = devices.find((x) => String(x.device_id) === String(id));
    return d ? `${d.device_name || d.terminal_code} (${d.vendor || ""})` : id;
  };

  return (
    <div className="p-6 w-full max-w-full min-w-0">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div>
          <h1 className="text-2xl font-semibold mb-1">Employee ↔ Device Mapping</h1>
          <p className="text-sm text-gray-500">
            Map device user IDs to employees. Enrollment metadata only — biometric templates stay on the device.
          </p>
        </div>
        <Link to="/attendance/devices" className="text-sm text-teal-700 underline font-medium">
          Device registry
        </Link>
      </div>

      {msg && <div className="mb-2 text-green-700 text-sm">{msg}</div>}
      {err && <div className="mb-2 text-red-600 text-sm">{err}</div>}

      <form onSubmit={onCreate} className="bg-white border rounded-xl p-4 grid md:grid-cols-2 gap-3 mb-6">
        <label className="text-sm grid gap-1">
          <span className="text-gray-600">Device</span>
          <select
            className="border rounded px-3 py-2"
            value={form.device_id}
            onChange={(e) => setForm({ ...form, device_id: e.target.value })}
          >
            <option value="">Select device</option>
            {devices.map((d) => (
              <option key={d.device_id} value={d.device_id}>
                {d.device_name || d.terminal_code} ({d.vendor})
              </option>
            ))}
          </select>
        </label>
        <label className="text-sm grid gap-1">
          <span className="text-gray-600">Employee</span>
          <select
            className="border rounded px-3 py-2"
            value={form.employee_id}
            onChange={(e) => setForm({ ...form, employee_id: e.target.value })}
          >
            <option value="">Select employee</option>
            {employees.map((e) => (
              <option key={e.employee_id} value={e.employee_id}>
                {e.employee_code} — {e.display_name || e.first_name}
              </option>
            ))}
          </select>
        </label>
        <label className="text-sm grid gap-1">
          <span className="text-gray-600">Device user ID</span>
          <input
            className="border rounded px-3 py-2"
            value={form.device_employee_code}
            onChange={(e) => setForm({ ...form, device_employee_code: e.target.value })}
            placeholder="PIN / user id on terminal"
          />
        </label>
        <label className="text-sm grid gap-1">
          <span className="text-gray-600">Verification method</span>
          <select
            className="border rounded px-3 py-2"
            value={form.verification_method}
            onChange={(e) => setForm({ ...form, verification_method: e.target.value })}
          >
            {["face", "fingerprint", "rfid", "pin", "hybrid"].map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </select>
        </label>
        <label className="text-sm grid gap-1">
          <span className="text-gray-600">RFID card (optional)</span>
          <input
            className="border rounded px-3 py-2"
            value={form.rfid_card}
            onChange={(e) => setForm({ ...form, rfid_card: e.target.value })}
          />
        </label>
        <div className="flex flex-wrap gap-4 items-end text-sm">
          <label className="inline-flex items-center gap-2">
            <input
              type="checkbox"
              checked={form.face_registered}
              onChange={(e) => setForm({ ...form, face_registered: e.target.checked })}
            />
            Face registered
          </label>
          <label className="inline-flex items-center gap-2">
            <input
              type="checkbox"
              checked={form.fingerprint_registered}
              onChange={(e) => setForm({ ...form, fingerprint_registered: e.target.checked })}
            />
            Fingerprint registered
          </label>
          <label className="inline-flex items-center gap-2">
            <input
              type="checkbox"
              checked={form.pin_set}
              onChange={(e) => setForm({ ...form, pin_set: e.target.checked })}
            />
            PIN set
          </label>
        </div>
        <div className="md:col-span-2">
          <button
            type="submit"
            disabled={busy}
            className="bg-teal-600 text-white py-2 px-4 rounded-lg disabled:opacity-60"
          >
            Create mapping
          </button>
        </div>
      </form>

      <div className="overflow-x-auto border rounded-xl bg-white">
        <table className="w-full text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="text-left p-3">Employee</th>
              <th className="text-left p-3">Device</th>
              <th className="text-left p-3">Device user ID</th>
              <th className="text-left p-3">Method</th>
              <th className="text-left p-3">Enrollment</th>
              <th className="text-left p-3">Flags</th>
              <th className="text-left p-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.map_id} className="border-t">
                <td className="p-3">{empLabel(r.employee_id)}</td>
                <td className="p-3">{deviceLabel(r.device_id)}</td>
                <td className="p-3 font-mono">{r.device_employee_code}</td>
                <td className="p-3">{r.verification_method}</td>
                <td className="p-3">
                  {r.enrollment_status}
                  {!r.is_active && <span className="text-red-600 text-xs ml-1">(inactive)</span>}
                </td>
                <td className="p-3 text-xs">
                  {[
                    r.face_registered && "face",
                    r.fingerprint_registered && "finger",
                    r.rfid_card && `rfid:${r.rfid_card}`,
                    r.pin_set && "pin",
                  ]
                    .filter(Boolean)
                    .join(" · ") || "—"}
                </td>
                <td className="p-3">
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      className="text-teal-700 underline"
                      onClick={() => toggleMethod(r, "face")}
                    >
                      Face
                    </button>
                    <button
                      type="button"
                      className="text-teal-700 underline"
                      onClick={() => toggleMethod(r, "fingerprint")}
                    >
                      Finger
                    </button>
                    {r.is_active && (
                      <button
                        type="button"
                        className="text-red-600 underline"
                        onClick={() => onRevoke(r.map_id)}
                      >
                        Revoke
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {!rows.length && (
              <tr>
                <td colSpan={7} className="p-4 text-center text-gray-500">
                  No mappings yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
